"use client";

import { useCallback } from "react";
import { useAuth } from "@wtc/ui";

/**
 * Shared pre-authed `fetch` for Hub client components.
 *
 * Returns a `fetch`-compatible function that attaches the current auth token
 * from the global `useAuth()` hook (`getAuthToken`) as a `Bearer` header and
 * sets `Content-Type: application/json` — the exact header construction the
 * MCP-connections / meeting-summary / user-directory call sites used inline.
 *
 * This centralizes the token source per `hub-api-auth-context` (never manual
 * header extraction) with no behavior change: same headers, same token source,
 * same request/response passthrough. When no token is available the
 * `Authorization` header is omitted (identical to the original call sites).
 */
export function useAuthedFetch(): (url: string, init?: RequestInit) => Promise<Response> {
  const { getAuthToken } = useAuth();

  return useCallback(
    async (url: string, init?: RequestInit) => {
      const token = getAuthToken();
      const headers = new Headers(init?.headers);
      headers.set("Content-Type", "application/json");
      if (token) headers.set("Authorization", `Bearer ${token}`);
      return fetch(url, { ...init, headers });
    },
    [getAuthToken],
  );
}
